import { doc, getDoc, type Firestore } from "firebase/firestore";
import { db } from "./firebase";
import type { Employee } from "./types";

const SESSION_KEY = "nha-tho:employee";

/** Strip everything except digits. Accepts "0901 234 567", "090-123-4567", etc. */
export function normalizePhone(input: string): string {
  return input.replace(/\D/g, "");
}

/** Format a 10-digit Vietnamese phone with a single space after the 4th digit. */
export function formatPhone(input: string): string {
  const digits = normalizePhone(input).slice(0, 10);
  if (digits.length <= 4) return digits;
  return `${digits.slice(0, 4)} ${digits.slice(4)}`;
}

export type LoginResult =
  | { ok: true; employee: Employee }
  | { ok: false; reason: "not-found" | "inactive" | "invalid-input" };

/** Soft-auth: look up the employee by phone in `employees/{phone}`. */
export async function loginWithPhone(
  rawInput: string,
  firestore: Firestore = db,
): Promise<LoginResult> {
  const phone = normalizePhone(rawInput);
  if (phone.length < 9) return { ok: false, reason: "invalid-input" };

  const snap = await getDoc(doc(firestore, "employees", phone));
  if (!snap.exists()) return { ok: false, reason: "not-found" };

  const data = snap.data() as Employee;
  if (!data.active) return { ok: false, reason: "inactive" };

  const employee: Employee = { ...data, phone };
  writeSession(employee);
  return { ok: true, employee };
}

export function getSession(): Employee | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Employee) : null;
  } catch {
    return null;
  }
}

export function writeSession(employee: Employee): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(employee));
}

export function logout(): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(SESSION_KEY);
}
